import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { DynamicWidgetConfig, QueryResult } from '@/types/dashboard';
import type { VisualizationClickEvent } from './DynamicVisualization';
import { formatMoneyDisplay, tryDecimal } from '@/lib/money';

interface NumberVisualizationProps {
  data: QueryResult;
  config: DynamicWidgetConfig;
  /** Callback when the number is clicked (DASH-24.2) */
  onSegmentClick?: (event: VisualizationClickEvent) => void;
}

/**
 * Format number based on aggregation type and field
 */
function formatNumber(value: number, aggregationType: string, field?: string): string {
  // Currency formatting for cost fields
  if (field?.toLowerCase().includes('cost') || field?.toLowerCase().includes('revenue')) {
    // 0 decimals is deliberate: a headline tile compares better without cents.
    const parsed = tryDecimal(value);
    return parsed === null ? '—' : formatMoneyDisplay(parsed, { places: 0 });
  }

  // Decimal for averages
  if (aggregationType === 'avg') {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }

  // Whole numbers for sum, count, min, max (no decimals)
  if (aggregationType === 'sum' || aggregationType === 'count' || aggregationType === 'min' || aggregationType === 'max') {
    return Math.round(value).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  // Default: comma separators
  return value.toLocaleString('en-US');
}

/**
 * Number Visualization (DASH-8.2, DASH-9.0, DASH-24.2)
 * Displays single metric with large number, label, and optional trend
 * Wrapped in React.memo for performance (DASH-8.7)
 * Polished in DASH-9.0: Balanced font sizes, gradient background, rounded trend badges
 * DASH-24.2: Added click handler for drill-down functionality
 */
export const NumberVisualization = React.memo(function NumberVisualization({ data, config, onSegmentClick }: NumberVisualizationProps) {
  // Extract the numeric value
  const value = typeof data.data === 'number' ? data.data : 0;

  // Format the number based on aggregation type
  const formattedValue = formatNumber(value, config.aggregation.type, config.aggregation.field);

  // Get trend data if available
  const trend = data.trend;

  // Handle click on the number (DASH-24.2)
  const handleClick = () => {
    if (onSegmentClick) {
      onSegmentClick({
        value: null, // No specific segment for number widgets
        index: null,
        data: { count: value },
      });
    }
  };

  return (
    <div
      className="h-full flex flex-col items-center justify-center relative cursor-pointer group"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Subtle background gradient for depth (DASH-9.0) */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-lg group-hover:from-primary/10 transition-colors" />

      {/* Main Number (DASH-9.0: Reduced to text-4xl to match legacy widgets) */}
      <div className="text-4xl font-bold text-primary mb-2 relative z-10 group-hover:scale-105 transition-transform">
        {formattedValue}
      </div>

      {/* Trend Comparison (DASH-9.0: Styled as rounded badge) */}
      {config.visualization.showTrend && trend && (
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted relative z-10">
          {trend.direction === 'up' && (
            <TrendingUp className="w-4 h-4 text-green-500" />
          )}
          {trend.direction === 'down' && (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
          {trend.direction === 'neutral' && (
            <Minus className="w-4 h-4 text-muted-foreground" />
          )}

          <div className="text-sm">
            <span className={`font-semibold ${
              trend.direction === 'up' ? 'text-green-500' :
              trend.direction === 'down' ? 'text-red-500' :
              'text-muted-foreground'
            }`}>
              {trend.change > 0 ? '+' : ''}{trend.change} ({trend.changePercent > 0 ? '+' : ''}{trend.changePercent}%)
            </span>
            <span className="text-muted-foreground ml-1">
              vs. previous period
            </span>
          </div>
        </div>
      )}
    </div>
  );
});
